/**
 * Deep Links Boot File
 *
 * Handles OAuth callbacks and other deep links on native platforms (Android/iOS).
 * When a deep link is received (e.g., from OAuth redirect, or a verified App
 * Link tapped in WhatsApp/email), this routes it to the appropriate handler
 * in the Vue app — both while the app is running (appUrlOpen) and on a cold
 * start (getLaunchUrl).
 */

import { defineBoot } from '#q-app';
import type { Router } from 'vue-router';
import { App } from '@capacitor/app';
import type { URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { platform, devLog, devError } from '@shared/vue/utils/debug';

/**
 * Route a received deep link into the SPA.
 *
 * OAuth callbacks get their dedicated handling (close the custom tab, carry
 * code/state/error to the callback page). Every other URL navigates to its
 * path — the App Links intent filter claims the whole tier domain, so this
 * is what lands an invite/join/{token} tap on the invitation page.
 *
 * `replace` avoids a broken Back stack when the app was cold-started by the
 * link (there is no meaningful "previous" in-app page to go back to).
 */
function routeDeepLink(router: Router, rawUrl: string, replace: boolean): void {
  try {
    const url = new URL(rawUrl);

    // Handle OAuth callback URLs
    // Supports both:
    // - https://test.emalino.com/auth/callback?code=xxx&state=xxx
    // - com.emalino://auth/callback?code=xxx&state=xxx
    if (url.pathname.includes('/auth/callback') || url.host === 'auth') {
      const code = url.searchParams.get('code');
      const state = url.searchParams.get('state');
      const error = url.searchParams.get('error');

      devLog('[Deep Links] OAuth callback detected', { code: !!code, state: !!state, error });

      // Close the OAuth browser window (Chrome Custom Tab / Safari View Controller)
      // This ensures the browser doesn't stay open after the OAuth flow completes
      void Browser.close().catch(() => {
        // Browser.close() may fail if browser wasn't opened via Browser.open()
        // This is fine - the deep link will still be handled
      });

      if (code || error) {
        const target = {
          path: '/auth/callback',
          query: {
            ...(code && { code }),
            ...(state && { state }),
            ...(error && { error }),
          },
        };
        void (replace ? router.replace(target) : router.push(target));
      }
      return;
    }

    // Handle other deep links by extracting the path
    // For example: https://test.emalino.com/invite/join/abc -> /invite/join/abc
    const path = url.pathname || '/';
    const query: Record<string, string> = {};

    // Convert search params to query object
    url.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    // Defence in depth: only navigate to a path that resolves to a real
    // route, not the catch-all 404. Any app can fire the custom-URL scheme,
    // so don't push an attacker-supplied path verbatim. (Router guards
    // already protect auth-required routes; this blocks arbitrary in-app
    // route injection beyond that.)
    const resolved = router.resolve(path);
    const isKnownRoute = resolved.matched.some((r) => r.path !== '/:catchAll(.*)*');
    const safePath = isKnownRoute ? path : '/';

    devLog('[Deep Links] Navigating to path:', safePath, 'with query:', query);
    const target = { path: safePath, query };
    void (replace ? router.replace(target) : router.push(target));
  } catch (err) {
    devError('[Deep Links] Failed to parse deep link URL:', err);
  }
}

export default defineBoot(async ({ router }) => {
  // Only set up deep link handling on native platforms
  if (!platform.isNativeApp()) {
    return;
  }

  devLog('[Deep Links] Setting up deep link listener for native app');

  // Listen for deep link events while the app is running
  await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    devLog('[Deep Links] Received deep link:', event.url);
    routeDeepLink(router, event.url, false);
  });

  // Cold start: the app was launched BY a link (App Link tap with the app
  // closed). appUrlOpen does not fire for this case — the URL arrives via
  // getLaunchUrl, and it must take the same generic routing as warm links
  // (previously only the OAuth callback was handled here, so a tapped
  // invitation opened the app on the default page and lost its path).
  const launchUrl = await App.getLaunchUrl();
  if (launchUrl?.url) {
    devLog('[Deep Links] App launched with URL:', launchUrl.url);
    routeDeepLink(router, launchUrl.url, true);
  }

  devLog('[Deep Links] Deep link listener configured');
});
